import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { bool, elementType, func, oneOf, string } from 'prop-types';

import PopupButtoned, {
  propTypes as popupButtonedPropTypes,
} from '~/components/PopupButtoned';
import PopupClassic, {
  propTypes as popupClassicPropTypes,
} from '~/components/PopupClassic';
import PopupMinimalistic, {
  propTypes as popupMinimalisticPropTypes,
} from '~/components/PopupMinimalistic';

import styles from './DialogWithTransition.module.scss';

export const TransitionsClassNames = {
  MODAL: 'modal',
  THIRD_MODAL: 'thirdModal',
  BOTTOM_TRANSITION: 'bottomPopupWithTransition',
};

function DialogWrapper({ containerClassName, children, ...props }) {
  return (
    <PopupClassic
      {...props}
      containerClassName={classnames(styles.popupContainer, containerClassName)}
      big={false}
    >
      {children}
    </PopupClassic>
  );
}
DialogWrapper.propTypes = popupClassicPropTypes;

const PopupTransitionWrapper = ({ children }) => {
  useEffect(() => {
    document.body.classList.add(styles.modalOpened);
    return () => {
      document.body.classList.remove(styles.modalOpened);
    };
  }, []);
  return children;
};

const BottomPopupTransitionWrapper = ({ children }) => {
  useEffect(() => {
    document.body.classList.add(styles.bottomModalOpened);
    return () => {
      document.body.classList.remove(styles.bottomModalOpened);
      document.body.classList.remove(styles.bottomModalClosed);
    };
  }, []);
  return children;
};

const BottomPopupTransitionFilterWrapper = ({ children }) => {
  useEffect(() => {
    document.body.classList.add(styles.bottomFilterModalOpened);
    return () => {
      document.body.classList.remove(styles.bottomFilterModalOpened);
    };
  }, []);
  return children;
};

const PopupWithTransition = ({
  Component,
  ComponentWrapper,
  closeAnimation,
  show,
  transitionClassName,
  onExited,
  ...props
}) =>
  createPortal(
    <CSSTransition
      mountOnEnter
      unmountOnExit
      classNames={transitionClassName}
      timeout={300}
      in={show}
      onExited={onExited}
    >
      <ComponentWrapper>
        <Component
          closeAnimation={closeAnimation}
          skipBlur
          fullscreen={false}
          {...props}
        />
      </ComponentWrapper>
    </CSSTransition>,
    document.body,
  );

const propTypes = {
  closeAnimation: func,
  show: bool.isRequired,
  transitionClassName: string,
  onExited: func,
};

PopupWithTransition.propTypes = {
  ...propTypes,
  Component: elementType.isRequired,
  ComponentWrapper: elementType.isRequired,
};

export const DialogWithTransition = (props) => (
  <PopupWithTransition
    Component={DialogWrapper}
    ComponentWrapper={PopupTransitionWrapper}
    transitionClassName={TransitionsClassNames.MODAL}
    {...props}
  />
);

DialogWithTransition.propTypes = {
  ...propTypes,
  ...popupClassicPropTypes,
};

export const PopupButtonedWithTransition = (props) => (
  <PopupWithTransition
    Component={PopupButtoned}
    ComponentWrapper={PopupTransitionWrapper}
    transitionClassName={TransitionsClassNames.MODAL}
    {...props}
  />
);

PopupButtonedWithTransition.propTypes = {
  ...propTypes,
  ...popupButtonedPropTypes,
};

export const PopupMinimalisticWithTransition = ({
  transitionClassName,
  ...props
}) => (
  <PopupWithTransition
    Component={PopupMinimalistic}
    ComponentWrapper={PopupTransitionWrapper}
    transitionClassName={transitionClassName || TransitionsClassNames.MODAL}
    {...props}
  />
);

PopupMinimalisticWithTransition.propTypes = {
  ...propTypes,
  ...popupMinimalisticPropTypes,
  transitionClassName: string,
};

export const componentWrappers = {
  PopupTransitionWrapper,
  BottomPopupTransitionWrapper,
  BottomPopupTransitionFilterWrapper,
};

export const AnimatedBottomPopup = (props) => (
  <PopupWithTransition
    Component={PopupMinimalistic}
    ComponentWrapper={
      props.componentWrapper || componentWrappers.BottomPopupTransitionWrapper
    }
    containerClassName={styles.bottomPopupModalContainer}
    transitionClassName={TransitionsClassNames.BOTTOM_TRANSITION}
    closeAnimation={() => {
      if (!props.disableCloseAnimation) {
        document.body.classList.add(styles.bottomModalClosed);
      }
    }}
    wrapperClassName={styles.popupWrapperClassName}
    {...props}
  />
);

AnimatedBottomPopup.propTypes = {
  ...propTypes,
  ...popupMinimalisticPropTypes,
  componentWrapper: oneOf(Object.keys(componentWrappers)),
  disableCloseAnimation: bool,
};
